import React, { useEffect, useState, useCallback } from "react";
import { PageHeader, Card, KPI, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import ReportCharts from "../components/ReportCharts";
import { Search, X, CalendarDays, AlertCircle, Pencil, Loader2, PackageCheck, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useAuth, isFieldRole } from "../context/AuthContext";

const STATUSES = ["ordered", "shipped", "delivered", "cancelled"];
const STATUS_TONE = { ordered: "amber", shipped: "teal", delivered: "emerald", cancelled: "rose" };
const monthOf = (s) => (s ? String(s).slice(0, 7) : null);

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
}

const charts = [
  { id: "red-status-mix", type: "donut", title: "Fulfilment status mix", valueLabel: "Redemptions", groupBy: (r) => r.fulfillment_status },
  { id: "red-top-rewards", type: "bar", title: "Top rewards redeemed", valueLabel: "Redemptions", groupBy: (r) => r.reward_name, sort: "value_desc", limit: 10, labelWidth: 150 },
  { id: "red-points-trend", type: "area", title: "Points redeemed over time", subtitle: "Monthly points", valueLabel: "Points", groupBy: (r) => monthOf(r.redeemed_at), value: (r) => r.points || 0, sort: "time", color: "#B45309" },
];

export default function RedemptionTracking() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ ordered: 0, shipped: 0, delivered: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState("");
  const { user } = useAuth();
  const readOnly = isFieldRole(user?.role);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const qs = new URLSearchParams();
      if (startDate) qs.set("start_date", startDate);
      if (endDate) qs.set("end_date", endDate);
      const { data } = await api.get(`/reports/redemptions?${qs.toString()}`);
      setRows(data.rows || []);
      setCounts(data.status_counts || { ordered: 0, shipped: 0, delivered: 0, cancelled: 0 });
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function quickStatus(row, status) {
    try {
      await api.patch(`/reports/redemptions/${row.id}`, { fulfillment_status: status });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, fulfillment_status: status } : r)));
    } catch (e) { setError(formatError(e)); }
  }

  async function saveEdit() {
    if (!editRow) return;
    setSaving(true); setError("");
    try {
      await api.patch(`/reports/redemptions/${editRow.id}`, {
        fulfillment_status: editRow.fulfillment_status,
        order_id: editRow.order_id || "",
        remark: editRow.remark || "",
      });
      setRows((prev) => prev.map((r) => (r.id === editRow.id ? { ...r, ...editRow } : r)));
      setEditRow(null);
    } catch (e) { setError(formatError(e)); }
    finally { setSaving(false); }
  }

  async function revert(row) {
    if (!window.confirm(`Revert this redemption?\n\n${row.physio_name} · ${row.reward_name}\n${fmt(row.points || 0)} points will be credited back to the physio's ACTIVE wallet and the order marked Cancelled/Reverted.\n\nThis cannot be undone.`)) return;
    setReverting(row.id); setError("");
    try {
      await api.post(`/reports/redemptions/${row.id}/revert`, {});
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, fulfillment_status: "cancelled", remark: "Reverted - Points Refunded" } : r)));
      setCounts((c) => ({ ...c, cancelled: (c.cancelled || 0) + 1 }));
    } catch (e) { setError(formatError(e)); }
    finally { setReverting(""); }
  }

  const columns = [
    { key: "redeemed_at", label: "Date", filter: "text", render: (r) => r.redeemed_at || "—" },
    { key: "physio_name", label: "Physio", filter: "text" },
    { key: "erp_outlet_id", label: "Outlet ERP ID", filter: "text", render: (r) => r.erp_outlet_id || <span className="text-slate-300">—</span> },
    { key: "mobile", label: "Mobile", filter: "text" },
    { key: "reward_name", label: "Reward / Product", filter: "text" },
    { key: "points", label: "Points", align: "right", render: (r) => fmt(r.points || 0) },
    { key: "delivery_address", label: "Delivery Address (WhatsApp)", filter: "text",
      render: (r) => r.delivery_address ? <span className="whitespace-normal max-w-[220px] inline-block leading-tight text-teal-800">{r.delivery_address}</span> : <span className="text-slate-300">—</span> },
    { key: "fulfillment_status", label: "Status", filter: "select", options: STATUSES,
      render: (r) => readOnly ? (
        <Tag tone={STATUS_TONE[r.fulfillment_status] || "slate"}><span className="capitalize">{r.fulfillment_status}</span></Tag>
      ) : (
        <select value={r.fulfillment_status} onChange={(e) => quickStatus(r, e.target.value)}
          className="!py-1 !text-[11px] !w-28 capitalize" data-testid={`redemption-status-${r.id}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ) },
    { key: "order_id", label: "Order ID", filter: "text", render: (r) => r.order_id || <span className="text-slate-300">—</span> },
    { key: "remark", label: "Remark", filter: "text",
      render: (r) => r.remark ? <span className="whitespace-normal max-w-[180px] inline-block leading-tight">{r.remark}</span> : <span className="text-slate-300">—</span> },
    { key: "actions", label: "", sortable: false, align: "right",
      render: (r) => readOnly ? <span className="text-slate-300 text-[11px]">—</span> : (
        <div className="inline-flex items-center gap-3 justify-end">
          <button onClick={() => setEditRow({ ...r })} className="text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 text-[11px]" data-testid={`redemption-edit-${r.id}`}>
            <Pencil size={12} /> Update
          </button>
          <button onClick={() => revert(r)} disabled={reverting === r.id} className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 text-[11px] disabled:opacity-50" data-testid={`redemption-revert-${r.id}`}>
            {reverting === r.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Revert
          </button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5" data-testid="redemption-tracking">
      <PageHeader
        kicker="Fulfilment · WhatsApp Redemptions"
        title="Redemption Tracking"
        subtitle={readOnly
          ? "Redemptions for your assigned physios — the reward, points spent, date, delivery address and current fulfilment status. This view is read-only."
          : "Every points redemption — the reward, points spent, date, the delivery address captured on WhatsApp, an order/tracking id, a remark and a fulfilment status you can update (Ordered → Shipped → Delivered / Cancelled), plus a Revert action that refunds the points."}
        testId="redemption-tracking-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Ordered" value={fmt(counts.ordered || 0)} testId="kpi-ordered" />
        <KPI label="Shipped" value={fmt(counts.shipped || 0)} testId="kpi-shipped" />
        <KPI label="Delivered" value={fmt(counts.delivered || 0)} testId="kpi-delivered" />
        <KPI label="Cancelled" value={fmt(counts.cancelled || 0)} testId="kpi-cancelled" />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="small-caps text-slate-500 inline-flex items-center gap-1"><CalendarDays size={12} /> From date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!py-1.5 !w-auto" data-testid="redemption-start-date" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="small-caps text-slate-500 inline-flex items-center gap-1"><CalendarDays size={12} /> To date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!py-1.5 !w-auto" data-testid="redemption-end-date" />
          </div>
          <button onClick={load} disabled={loading} className="btn-primary !py-1.5 inline-flex items-center gap-1.5" data-testid="redemption-apply">
            <Search size={13} /> Apply
          </button>
          <button onClick={() => { setStartDate(""); setEndDate(""); setTimeout(load, 0); }} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5" data-testid="redemption-clear">
            <X size={13} /> Clear
          </button>
          <div className="ml-auto text-[12px] text-slate-500" data-testid="redemption-count">
            {loading ? "Loading…" : `${fmt(rows.length)} redemption${rows.length === 1 ? "" : "s"}`}
          </div>
        </div>
        {error && <div className="mt-3 text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={12} /> {error}</div>}
      </Card>

      {!loading && rows.length > 0 && <ReportCharts rows={rows} charts={charts} testId="redemption-tracking" />}

      <Card>
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          testIdPrefix="redemption-tracking"
          searchableKeys={["physio_name", "erp_outlet_id", "mobile", "reward_name", "order_id", "delivery_address"]}
          exportFilename="redemption-tracking"
          paginated
          pageSize={25}
        />
      </Card>

      {editRow && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="redemption-edit-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="serif-display text-xl inline-flex items-center gap-2"><PackageCheck size={18} className="text-teal-700" /> Update Redemption</h3>
              <button onClick={() => setEditRow(null)}><X size={18} /></button>
            </div>
            <div className="text-[12px] text-slate-500">
              <div className="font-medium text-slate-800">{editRow.physio_name} · {editRow.mobile}</div>
              <div>{editRow.reward_name} · {fmt(editRow.points || 0)} pts</div>
              {editRow.delivery_address && <div className="mt-1 text-teal-800">📍 {editRow.delivery_address}</div>}
            </div>
            <div>
              <label className="small-caps text-slate-500">Fulfilment status</label>
              <select value={editRow.fulfillment_status} onChange={(e) => setEditRow({ ...editRow, fulfillment_status: e.target.value })} className="w-full capitalize" data-testid="redemption-edit-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="small-caps text-slate-500">Order / Tracking ID</label>
              <input type="text" value={editRow.order_id || ""} onChange={(e) => setEditRow({ ...editRow, order_id: e.target.value })} className="w-full" placeholder="e.g. AWB / courier tracking no." data-testid="redemption-edit-orderid" />
            </div>
            <div>
              <label className="small-caps text-slate-500">Remark</label>
              <textarea value={editRow.remark || ""} onChange={(e) => setEditRow({ ...editRow, remark: e.target.value })} rows={3} className="w-full" placeholder="Internal note about this fulfilment…" data-testid="redemption-edit-remark" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditRow(null)} className="btn-secondary !py-2">Cancel</button>
              <button onClick={saveEdit} disabled={saving} className="btn-teal !py-2 inline-flex items-center gap-2" data-testid="redemption-edit-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
